import React from "react";

interface DiagnosticsInfo {
  device_code: string;
  device_name: string | null;
  playlist_version: number;
  online: boolean;
  cache_bytes: number;
  cache_count: number;
  last_sync: string | null;
  current_media: string;
  api_url: string;
  app_version: string;
  last_command: string | null;
  last_command_result: string | null;
}

const formatBytes = (b: number): string => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
};

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{
    display: "flex", justifyContent: "space-between", gap: 24,
    padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.08)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 13,
  }}>
    <span style={{ color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 11 }}>
      {label}
    </span>
    <span style={{ color: "#fff", textAlign: "right", maxWidth: "60%", wordBreak: "break-all" }}>
      {value}
    </span>
  </div>
);

const Diagnostics: React.FC<{ info: DiagnosticsInfo; onClose: () => void }> = ({ info, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.85)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 32, cursor: "default",
    }}>
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "100%", maxWidth: 720,
        background: "linear-gradient(180deg, rgba(15,23,42,0.95) 0%, rgba(2,6,23,0.95) 100%)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 12, padding: 24, color: "#fff",
        boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: "linear-gradient(135deg, #7c3aed, #8b5cf6)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 800,
        }}>VM</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Diagnostics</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
            Press Esc or click outside to close
          </div>
        </div>
        <div style={{
          padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
          background: info.online ? "rgba(16,185,129,0.18)" : "rgba(239,68,68,0.18)",
          color: info.online ? "#a7f3d0" : "#fca5a5",
          border: `1px solid ${info.online ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.4)"}`,
        }}>
          {info.online ? "ONLINE" : "OFFLINE"}
        </div>
      </div>

      <Row label="Device Code" value={info.device_code} />
      <Row label="Device Name" value={info.device_name || "—"} />
      <Row label="Playlist Version" value={info.playlist_version} />
      <Row label="Online Status" value={info.online ? "connected" : "disconnected"} />
      <Row label="Cache Size" value={`${formatBytes(info.cache_bytes)} · ${info.cache_count} items`} />
      <Row label="Last Sync" value={info.last_sync ? new Date(info.last_sync).toLocaleString() : "never"} />
      <Row label="Current Media" value={info.current_media || "—"} />
      <Row label="Last Command" value={info.last_command || "—"} />
      <Row label="Last Command Result" value={info.last_command_result || "—"} />
      <Row label="API URL" value={info.api_url || "—"} />
      <Row label="App Version" value={info.app_version} />
    </div>
  </div>
);

export type { DiagnosticsInfo };
export default Diagnostics;
